import { RejectionItem } from '../model';

/**
 * True for a 422 TRACES business rejection with at least one located error (app#287).
 *
 * There is no `traces_rejected` error type (decision E3a) — a TRACES business rejection is the
 * same `error_type: 'eudr_rejected'` the geo-precheck uses, distinguished only by carrying
 * `errors`. The array is always present on `eudr_rejected` but MAY be empty (a rejection TRACES
 * gave no located detail for); an empty array has nothing for the caller to render, so it does
 * not count as "rejected" here — the caller falls back to its generic error toast instead.
 */
export function isTracesRejected(e: unknown): e is { error: { error_type: 'eudr_rejected'; errors: RejectionItem[] } } {
  const body = (e as { error?: { error_type?: string; errors?: unknown } } | undefined)?.error;
  return body?.error_type === 'eudr_rejected' && Array.isArray(body.errors) && body.errors.length > 0;
}

/**
 * A DDS job that carries a submission marker (app#374): `dds_submission_in_progress` (409 — a
 * submission is running or being verified, this call sent nothing) or
 * `dds_submission_unconfirmed` (502 — THIS submit ended without a known outcome). Either way the
 * job is locked now and the caller must reload it instead of offering another attempt.
 */
export function isDdsSubmissionPending(
  e: unknown
): e is { error: { error_type: 'dds_submission_in_progress' | 'dds_submission_unconfirmed' } } {
  const type = (e as { error?: { error_type?: string } } | undefined)?.error?.error_type;
  return type === 'dds_submission_in_progress' || type === 'dds_submission_unconfirmed';
}

/**
 * Toast an external-service failure with a specific message when `error_type` is recognized,
 * falling back to a caller-supplied message otherwise. Shared by every caller that used to carry
 * its own verbatim copy (attr-report-detail, declaration-list, …) — `notification` only needs the
 * `error(message)` shape, not the concrete `NotificationService` type, to keep this dependency-free.
 */
export function notifyExternalError(
  notification: { error: (message: string) => unknown },
  e: unknown,
  fallback: string
): void {
  const errorType = (e as { error?: { error_type?: string } } | undefined)?.error?.error_type;
  notification.error(friendlyExternalMessage(errorType) ?? fallback);
}

export function friendlyExternalMessage(errorType: string | undefined): string | null {
  switch (errorType) {
    case 'external_unavailable':
      return 'A required service is temporarily unavailable (busy or out of capacity). Please try again later.';
    case 'external_not_configured':
      return 'This feature is not available right now — support has been notified.';
    case 'external_bad_request':
      return "The request couldn't be processed by an external service.";
    case 'document_in_use':
      return "This document is still in use elsewhere and can't be deleted. Remove it from those references first.";
    case 'database_unavailable':
      return 'The database is temporarily unavailable. Please try again in a moment.';
    default:
      return null;
  }
}

/**
 * Maps a snapshot-usability verdict reason (or capture-failure kind) to English text for the
 * "This article is disabled …" banner and the news-archive Reason cells. Anything not a
 * recognised reason reads as a plain capture failure (FAILED / transient / auth / unexpected).
 *
 * 'OK' is a reason too — the server writes it on a clean capture — so it MUST stay a case: the
 * archive views render the text next to every reason, not just the bad ones, and the fallback
 * made a successful capture read "OK (capture failed)".
 */
export function archiveReasonMessage(reason: string | undefined): string {
  switch (reason) {
    case 'OK':
      return 'usable snapshot';
    case 'BANNER':
      return 'consent/login overlay still visible';
    case 'EMPTY':
      return 'empty page';
    case 'ERROR_PAGE':
      return 'error page';
    case 'PAYWALL':
      return 'paywall';
    case 'UNRELATED':
      return 'unrelated page';
    case 'REDIRECTED':
      return 'redirected away';
    case 'BLOCKED':
      return 'blocked by the site';
    case 'UNSUPPORTED_FILE_TYPE':
      return 'unsupported file type';
    case 'FILE_TOO_LARGE':
      return 'file too large';
    case 'OTHER':
      return 'flagged by the snapshot check';
    default:
      return 'capture failed';
  }
}
